import { ManfrediteCvApplication } from '@asgard/common-dto';
import {
  faEnvelopeOpenText,
  faEye,
  faFileLines,
  faPen
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PublicProfileComponent from 'components/publicProfile/PublicProfileComponent';
import Button from 'components/shared/Button.styled';
import ButtonLink from 'components/shared/ButtonLink.styled';
import Dropdown from 'components/shared/Dropdown';
import { useI18n } from 'hooks/useI18n';
import { PublicManfrediteJobApplicationResume } from 'interfaces/manfredite.interface';
import { PublicManfredite } from 'interfaces/public-manfredite.interface';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { getTranslatedText } from 'services/multilingual.service';
import { APP_URLS, getAppUrl } from 'utils/app-urls';
import CVApplicationsCoverLetterModalComponent from './coverLetter/CVApplicationsCoverLetterModalComponent';
import {
  StyledFooter,
  StyledFooterActions,
  StyledFooterButtons,
  StyledFooterEdit,
  StyledFooterHeader,
  StyledFooterMobileButtons,
  StyledFooterMobileDropUp,
  StyledFooterTag,
  StyledWrapper
} from './CVApplicationTermsAcceptedComponent.styled';
import CVApplicationsTermsModalComponent from './termsAndConditions/CVApplicationsTermsModalComponent';

type Props = {
  publicManfredite: PublicManfredite;
  cvApplication: ManfrediteCvApplication;
  jobApplicationResume: PublicManfrediteJobApplicationResume;
  previewMode?: boolean;
};

const CVApplicationTermsAcceptedComponent: React.FC<Props> = ({
  publicManfredite,
  cvApplication,
  jobApplicationResume,
  previewMode = false
}) => {
  const { t } = useI18n('cv-applications-view');
  const { locale } = useRouter();

  const [termsModalOpened, setTermsModalOpened] = useState(false);
  const [coverLetterModalOpened, setCoverLetterModalOpened] = useState(false);

  const hasCoverLetter = cvApplication.enableCoverLetter;
  const hasPrivacyTerms = cvApplication.enablePrivacyTerms;

  useEffect(() => {
    setCoverLetterModalOpened(hasCoverLetter);
  }, [hasCoverLetter]);

  const [mobileActionsMenuOpen, setMobileActionsMenuOpen] = useState(false);
  const dropdown = useRef<HTMLDivElement>(null);

  const handleCloseMobileActionsMenuClick = (e: MouseEvent): void => {
    if (dropdown.current?.contains(e.target as Node)) {
      return;
    }
    setMobileActionsMenuOpen(false);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleCloseMobileActionsMenuClick);

    return () => {
      document.removeEventListener(
        'mousedown',
        handleCloseMobileActionsMenuClick
      );
    };
  }, []);

  const mobileActions = [
    {
      label: t('cvApplicationsView.termsAccepted.footer.privacyTerms_short'),
      value: t('cvApplicationsView.termsAccepted.footer.privacyTerms_short'),
      action: () => setTermsModalOpened(true)
    },

    {
      label: t('cvApplicationsView.termsAccepted.footer.coverLetter_short'),
      value: t('cvApplicationsView.termsAccepted.footer.coverLetter_short'),
      action: () => setCoverLetterModalOpened(true)
    }
  ];

  const withoutTermsAndLetter = !hasPrivacyTerms && !hasCoverLetter;

  return (
    <StyledWrapper>
      <PublicProfileComponent
        publicManfredite={publicManfredite}
        slug={publicManfredite.slug}
        type={cvApplication.view}
        isDefaultView={false}
        sendVisitTrackEvent={false}
      />

      {(hasPrivacyTerms || hasCoverLetter || previewMode) && (
        <StyledFooter previewMode={previewMode}>
          {previewMode && !withoutTermsAndLetter && (
            <StyledFooterHeader>
              {t('cvApplicationsView.termsAccepted.footer.header')}
            </StyledFooterHeader>
          )}

          <StyledFooterActions>
            {previewMode && withoutTermsAndLetter && (
              <StyledFooterTag>
                {t('cvApplicationsView.termsAccepted.footer.header')}
              </StyledFooterTag>
            )}
            <StyledFooterButtons>
              {hasPrivacyTerms && (
                <Button
                  sizes="sm"
                  variant="secondaryInverse"
                  onClick={() => setTermsModalOpened(true)}
                  aria-label={t(
                    'cvApplicationsView.termsAccepted.footer.privacyTerms'
                  )}
                >
                  <FontAwesomeIcon aria-hidden={true} icon={faFileLines} />
                  <span aria-hidden={true}>
                    {t('cvApplicationsView.termsAccepted.footer.privacyTerms')}
                  </span>
                </Button>
              )}

              {hasCoverLetter && (
                <Button
                  sizes="sm"
                  variant="secondaryInverse"
                  onClick={() => setCoverLetterModalOpened(true)}
                  aria-label={t(
                    'cvApplicationsView.termsAccepted.footer.coverLetter'
                  )}
                >
                  <FontAwesomeIcon
                    aria-hidden={true}
                    icon={faEnvelopeOpenText}
                  />
                  <span aria-hidden={true}>
                    {t('cvApplicationsView.termsAccepted.footer.coverLetter')}
                  </span>
                </Button>
              )}
            </StyledFooterButtons>
            <StyledFooterMobileButtons>
              {((hasPrivacyTerms && !hasCoverLetter && previewMode) ||
                (hasPrivacyTerms && !previewMode)) && (
                <Button
                  sizes="sm"
                  variant="secondaryInverse"
                  onClick={() => setTermsModalOpened(true)}
                  aria-label={t(
                    'cvApplicationsView.termsAccepted.footer.privacyTerms'
                  )}
                >
                  <FontAwesomeIcon aria-hidden={true} icon={faFileLines} />
                  <span aria-hidden={true}>
                    {t(
                      'cvApplicationsView.termsAccepted.footer.privacyTerms_short'
                    )}
                  </span>
                </Button>
              )}

              {((hasCoverLetter && !hasPrivacyTerms && previewMode) ||
                (hasCoverLetter && !previewMode)) && (
                <Button
                  sizes="sm"
                  variant="secondaryInverse"
                  onClick={() => setCoverLetterModalOpened(true)}
                  aria-label={t(
                    'cvApplicationsView.termsAccepted.footer.coverLetter'
                  )}
                >
                  <FontAwesomeIcon
                    aria-hidden={true}
                    icon={faEnvelopeOpenText}
                  />
                  <span aria-hidden={true}>
                    {t(
                      'cvApplicationsView.termsAccepted.footer.coverLetter_short'
                    )}
                  </span>
                </Button>
              )}

              {hasCoverLetter && hasPrivacyTerms && previewMode && (
                <StyledFooterMobileDropUp ref={dropdown}>
                  <Button
                    sizes="sm"
                    variant="secondaryInverse"
                    aria-controls="cv-application-preview-actions-dropdown"
                    onClick={() =>
                      setMobileActionsMenuOpen(!mobileActionsMenuOpen)
                    }
                    aria-label={t(
                      'cvApplicationsView.termsAccepted.footer.showActions'
                    )}
                    isTrigger
                  >
                    <FontAwesomeIcon aria-hidden={true} icon={faEye} />
                    <span aria-hidden={true}>
                      {t('cvApplicationsView.termsAccepted.footer.showActions')}
                    </span>
                  </Button>
                  <Dropdown
                    id="cv-application-actions-dropdown"
                    options={mobileActions}
                    isDropUp
                    isAlignLeft
                    style={{
                      display: mobileActionsMenuOpen ? 'block' : 'none'
                    }}
                  />
                </StyledFooterMobileDropUp>
              )}
            </StyledFooterMobileButtons>

            {previewMode && (
              <StyledFooterEdit>
                <ButtonLink
                  sizes="sm"
                  variant="tertiaryInverse"
                  href={`/${locale}${getAppUrl(
                    APP_URLS.portalV2Dashboard,
                    locale
                  )}`}
                  target="_blank"
                >
                  <FontAwesomeIcon aria-hidden={true} icon={faPen} />
                  <span>
                    {t('cvApplicationsView.termsAccepted.footer.editProfile')}
                  </span>
                </ButtonLink>
              </StyledFooterEdit>
            )}
          </StyledFooterActions>
        </StyledFooter>
      )}

      {hasPrivacyTerms && jobApplicationResume && (
        <CVApplicationsTermsModalComponent
          isOpen={termsModalOpened}
          alreadyAccepted
          close={() => {
            setTermsModalOpened(false);
          }}
          jobApplicationResume={jobApplicationResume}
        />
      )}

      {hasCoverLetter && (
        <CVApplicationsCoverLetterModalComponent
          coverLetter={getTranslatedText(cvApplication.coverLetter, locale)}
          isOpen={coverLetterModalOpened}
          close={() => {
            setCoverLetterModalOpened(false);
          }}
        />
      )}
    </StyledWrapper>
  );
};

export default CVApplicationTermsAcceptedComponent;
